// creature-defense-delta.ts — Janus C1 (2026-09-24): the gear-passive share of finalize_creature_arr()'s FEAT / PASSIVE-ABILITY
// GRANTED DEFENSES fold, applied as a DELTA to the prior computed.creature.defenses when a passive surfaced from equipped gear
// enters or leaves (an equip / unequip of a Boots of Striding and Springing: "Immune to Status Effect: Encumbered").
//
// The fold writes first-wins SOURCED entries (add_sourced_status_defense_entry / add_damage_type_defense_entry) in a fixed order:
// the host's own layers, permanent statuses, feats, then passive_ability entries in bucket order — and compute_defenses' carrier
// folds append after. Gear deltas keep named refusals for source order they cannot establish.
// Permanent-status deltas use the held permanent-status order and retain every independent contributor.

import { foldCreatureDefenseEntries, normalizeDefenseSource } from './creature-defense';
import { readLayerListEntryUuid } from './inventory-raw-readers';

type Row = Record<string, unknown>;
const isObj = (v: unknown): v is Row => v !== null && typeof v === 'object' && !Array.isArray(v);
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();

/** FT_CREATURE_DEFENSE_SLOTS (build_item_arr.php): layer → computed store + the entry's key field. */
export const DEFENSE_SLOTS: Record<string, { slot: string; key: 'item_layer_effect' | 'damage_type' }> = {
    damage_type_resistance: { slot: 'resistance', key: 'damage_type' },
    damage_type_immunity: { slot: 'immune', key: 'damage_type' },
    damage_type_vulnerability: { slot: 'vulnerable', key: 'damage_type' },
    immune_status_effect: { slot: 'immune_status', key: 'item_layer_effect' },
    contesting_roll_adv_vs_status_effect: { slot: 'contesting_roll_adv_vs_status_effect', key: 'item_layer_effect' },
    contesting_roll_dis_vs_status_effect: { slot: 'contesting_roll_dis_vs_status_effect', key: 'item_layer_effect' },
};

export interface GearPassiveDefenseChange {
    passiveId: string;
    name: string;
    /** the passive definition's own layer fragments, per defense layer (raw values, in authored order) */
    fragments: Record<string, unknown[]>;
}
export interface DefenseDeltaInput {
    defenses: Row | undefined;
    hostId: string;
    /** passive_ability bucket entry ids in bucket order — POST for entering passives, PRIOR for leaving ones */
    postBucket: string[];
    priorBucket: string[];
    /** passive ids still ACTIVE after the mutation (any source) — a leaving occurrence another copy still grants keeps its defenses */
    postActive: string[];
    entering: GearPassiveDefenseChange[];
    leaving: GearPassiveDefenseChange[];
}
export type DefenseDeltaResult = { ok: true; defenses: Row | undefined } | { ok: false; reason: string };

/** A stored entry's `key`, else layer_list_entry_uuid (item_layer_effect → bucketed shell items.item_id → item_id; a `|suffix` dropped).
 *  Codex review 2026-09-25: the shell shape read as '' and its immunity was silently skipped. */
const entryUuid = (fragment: unknown, key: string): string => {
    const head = (v: unknown): string => typeof v === 'string' && v !== '' ? v.split('|', 1)[0]! : '';
    if (isObj(fragment)) return head(fragment[key]) || head(fragment.item_layer_effect) || head(isObj(fragment.items) ? (fragment.items as Row).item_id : undefined) || head(fragment.item_id);
    return head(fragment);
};

export interface PermanentStatusDefenseChange {
    id: string;
    name: string;
    fragments: Record<string, unknown[]>;
}
/** Permanent statuses rank after host-authored defenses and before feats,
 * passives and carriers. Retract only the departing status's contribution;
 * a surviving source takes over the same entry when its first source leaves.
 * Untouched stores and rows retain identity, including on a no-op.
 */
export function applyPermanentStatusDefenseDelta(input: {
    defenses: Row | undefined;
    hostId: string;
    statusIds: readonly string[];
    entering: readonly PermanentStatusDefenseChange[];
    leaving: readonly string[];
}): Row | undefined {
    let defenses = input.defenses;
    const leaving = new Set(input.leaving.map(upper));
    const rank = (source: unknown): number => {
        if (!isObj(source)) return -1; // Legacy scalar defenses are host-authored.
        if (upper(source.source_item_id) === upper(input.hostId)) return -1;
        const index = input.statusIds.findIndex(id => upper(id) === upper(source.source_item_id));
        return source.source_kind === 'status_effect' && index >= 0 ? index : Number.MAX_SAFE_INTEGER;
    };
    for (const layer of [...STATUS_DAMAGE_DEFENSE_LAYERS, 'immune_status_effect']) {
        const spec = DEFENSE_SLOTS[layer]!, family = spec.key === 'damage_type' ? 'damage' : 'status';
        const initial = Array.isArray(defenses?.[spec.slot]) ? defenses[spec.slot] as unknown[] : [];
        let entries = initial;
        const removed = (source: Row) => source.source_kind === 'status_effect' && leaving.has(upper(source.source_item_id));
        if (leaving.size) {
            const next: unknown[] = [];
            for (const entry of entries) {
                if (!isObj(entry)) { next.push(entry); continue; }
                const sources = Array.isArray(entry.all_sources) ? entry.all_sources.filter(isObj) : [];
                const rest = sources.filter(source => !removed(source));
                if (removed(entry)) {
                    const [first, ...tail] = rest;
                    if (first) next.push({ [spec.key]: entry[spec.key], ...first, ...(tail.length ? { all_sources: tail } : {}) });
                } else if (rest.length !== sources.length) {
                    const row = { ...entry };
                    if (rest.length) row.all_sources = rest; else delete row.all_sources;
                    next.push(row);
                } else next.push(entry);
            }
            if (next.length !== entries.length || next.some((entry, i) => entry !== entries[i])) entries = next;
        }
        for (const status of input.entering) for (const fragment of status.fragments[layer] ?? []) {
            const id = isObj(fragment) && typeof fragment[spec.key] === 'string' ? fragment[spec.key] as string : readLayerListEntryUuid(fragment);
            if (!id) continue;
            const source = { source_item_id: status.id, source_name: status.name, source_kind: 'status_effect' };
            const at = entries.findIndex(entry => upper(entryUuid(entry, spec.key)) === upper(id));
            const existing = at < 0 ? undefined : entries[at];
            if (isObj(existing) && [existing, ...(Array.isArray(existing.all_sources) ? existing.all_sources : [])]
                .some(meta => isObj(meta) && upper(meta.source_item_id) === upper(status.id))) continue;
            let next: unknown;
            if (isObj(existing) && rank(source) < rank(existing)) {
                const contributors = [normalizeDefenseSource(existing, family), ...(Array.isArray(existing.all_sources) ? existing.all_sources : [])]
                    .filter(isObj).filter(meta => upper(meta.source_item_id) !== upper(status.id));
                next = { [spec.key]: id, ...source, all_sources: contributors };
            } else {
                const storedId = existing === undefined ? id : entryUuid(existing, spec.key);
                next = foldCreatureDefenseEntries({ family, initialEntries: existing === undefined ? [] : [existing], events: [{ uuid: storedId, source }] })[0];
            }
            if (next === existing) continue;
            if (isObj(next) && Array.isArray(next.all_sources)) next.all_sources = next.all_sources.slice().sort((a: Row, b: Row) => rank(a) - rank(b));
            entries = entries.slice();
            if (at >= 0) entries.splice(at, 1);
            const position = entries.findIndex(entry => isObj(entry) && rank(entry) > rank(next as Row));
            entries.splice(position < 0 ? entries.length : position, 0, next);
        }
        if (entries !== initial) {
            entries = entries.slice().sort((a, b) => rank(a) - rank(b));
            if (defenses === input.defenses) defenses = { ...defenses };
            if (entries.length) defenses![spec.slot] = entries; else delete defenses![spec.slot];
        }
    }
    return defenses && Object.keys(defenses).length ? defenses : undefined;
}

export function applyGearPassiveDefenseDelta(input: DefenseDeltaInput): DefenseDeltaResult {
    const defenses: Row = { ...input.defenses };
    const rankOf = (entry: unknown, bucket: string[]): number | null => {
        if (!isObj(entry)) return null;
        const sid = upper(entry.source_item_id), kind = String(entry.source_kind ?? '');
        if (sid !== '' && sid === upper(input.hostId)) return 0;
        if (kind === 'status_effect') return 1;
        if (kind === 'feat') return 2;
        if (kind === 'passive_ability') { const i = bucket.indexOf(sid); return i < 0 ? null : 3 + i; }
        return null;
    };
    const storeOf = (slot: string): unknown[] => Array.isArray(defenses[slot]) ? defenses[slot] as unknown[] : [];
    const mentions = (entry: unknown, id: string): boolean => isObj(entry) && Array.isArray(entry.all_sources)
        && (entry.all_sources as unknown[]).some(s => isObj(s) && upper(s.source_item_id) === id);

    // LEAVING first (against the prior order): an entry whose primary source is the passive, never shared
    for (const change of input.leaving) {
        const id = upper(change.passiveId);
        // PHP folds by passive id: a surviving active copy (another gear row, the host) still grants — refuse rather than model it
        // (Codex review 2026-09-24)
        if (input.postActive.map(upper).includes(id)) return { ok: false, reason: 'unported:defenses:surviving-copy' };
        for (const [layer, spec] of Object.entries(DEFENSE_SLOTS)) {
            // (N4c 2026-10-02) the passive folded into ANOTHER source's entry as a contributor: the rebuild folds the same sources in the
            // same order minus this one — drop it from all_sources (an emptied list is elided, the stored lean shape every new entry has);
            // the entry and its position are the earlier source's
            const store = storeOf(spec.slot);
            if (store.some(e => mentions(e, id))) {
                defenses[spec.slot] = store.map(e => {
                    if (!mentions(e, id) || upper((e as Row).source_item_id) === id) return e;
                    const rest = ((e as Row).all_sources as unknown[]).filter(s => !(isObj(s) && upper(s.source_item_id) === id));
                    const out: Row = { ...(e as Row) };
                    if (rest.length) out.all_sources = rest; else delete out.all_sources;
                    return out;
                });
            }
        }
        for (const [layer, spec] of Object.entries(DEFENSE_SLOTS)) {
            const store = storeOf(spec.slot);
            if (store.some(e => mentions(e, id))) return { ok: false, reason: `unported:defenses:shared-source:${layer}` };
            const kept = store.filter(e => !(isObj(e) && upper(e.source_item_id) === id && e.source_kind === 'passive_ability'));
            for (const e of store) if (isObj(e) && upper(e.source_item_id) === id && Array.isArray(e.all_sources) && e.all_sources.length)
                return { ok: false, reason: `unported:defenses:shared-source:${layer}` };
            const expected = (change.fragments[layer] ?? []).map(f => entryUuid(f, spec.key)).filter(Boolean);
            const removed = store.filter(e => !kept.includes(e)).map(e => entryUuid(e, spec.key));
            // every removed entry must be one the passive authors (a partial first-wins would have kept another source's)
            if (removed.some(u => !expected.includes(u))) return { ok: false, reason: `unported:defenses:leave-mismatch:${layer}` };
            if (kept.length) defenses[spec.slot] = kept; else delete defenses[spec.slot];
        }
    }
    // ENTERING in post bucket order: insert each new entry after the last entry ranked at or before the passive
    const entering = [...input.entering].sort((a, b) => input.postBucket.indexOf(upper(a.passiveId)) - input.postBucket.indexOf(upper(b.passiveId)));
    for (const change of entering) {
        const id = upper(change.passiveId), rank = input.postBucket.indexOf(id);
        if (rank < 0) return { ok: false, reason: 'unported:defenses:passive-order' };
        for (const [layer, spec] of Object.entries(DEFENSE_SLOTS)) {
            const fragments = change.fragments[layer] ?? [];
            if (!fragments.length) continue;
            // a store the fold CREATES (2026-09-25, R-042): appended as a new key — the served == rebuild comparison is canonical (key order
            // inside computed.creature.defenses carries no meaning for any reader)
            if (defenses[spec.slot] !== undefined && !Array.isArray(defenses[spec.slot])) return { ok: false, reason: `unported:defenses:new-slot:${layer}` };
            if (defenses[spec.slot] === undefined) defenses[spec.slot] = [];
            const store = [...storeOf(spec.slot)];
            const ranks = store.map(e => rankOf(e, input.postBucket));
            if (ranks.some(r => r === null)) return { ok: false, reason: `unported:defenses:unknown-source:${layer}` };
            for (let i = 1; i < ranks.length; i++) if ((ranks[i] as number) < (ranks[i - 1] as number)) return { ok: false, reason: `unported:defenses:order:${layer}` };
            let at = ranks.filter(r => (r as number) <= 3 + rank).length;
            for (const fragment of fragments) {
                const uuid = entryUuid(fragment, spec.key);
                if (!uuid) return { ok: false, reason: `unported:defenses:fragment:${layer}` };
                const dup = store.findIndex(e => entryUuid(e, spec.key) === uuid);
                if (dup >= 0) {
                    // (N4c 2026-10-02 — was a refusal) add_sourced_status_defense_entry is FIRST-WINS: an entry an EARLIER-ranked source already
                    // created keeps its place and gains this passive in all_sources (appended — exact only when every contributor already listed
                    // is ranked before it too); a later-ranked owner would hand the entry to this passive in a rebuild → still refused
                    const ex = store[dup];
                    if (!isObj(ex)) return { ok: false, reason: `unported:defenses:first-wins:${layer}` };
                    const contribs = Array.isArray(ex.all_sources) ? ex.all_sources as unknown[] : [];
                    if (upper(ex.source_item_id) === id || contribs.some(c => isObj(c) && upper(c.source_item_id) === id)) continue;
                    const exRank = rankOf(ex, input.postBucket);
                    if (exRank === null) return { ok: false, reason: `unported:defenses:first-wins:${layer}` };
                    if (exRank > 3 + rank) {
                        // the entering passive ranks EARLIER: a rebuild creates the entry at the passive's place with the passive as its
                        // source, and folds the old owner (then its own contributors, in order) into all_sources — foldDefenseContributors
                        const moved: Row = { [spec.key]: uuid, source_item_id: change.passiveId };
                        if (change.name) moved.source_name = change.name;
                        moved.source_kind = 'passive_ability';
                        const folded = foldDefenseContributors(ex, change.passiveId);
                        if (folded.length) moved.all_sources = folded;
                        store.splice(dup, 1);
                        store.splice(at, 0, moved); at += 1;
                        continue;
                    }
                    if (contribs.some(c => { const r = rankOf(c, input.postBucket); return r === null || r >= 3 + rank; }))
                        return { ok: false, reason: `unported:defenses:first-wins:${layer}` };
                    const meta: Row = { source_item_id: change.passiveId };
                    if (change.name) meta.source_name = change.name;
                    meta.source_kind = 'passive_ability';
                    store[dup] = { ...ex, all_sources: [...contribs, meta] };
                    continue;
                }
                const entry: Row = { [spec.key]: uuid, source_item_id: change.passiveId };
                if (change.name) entry.source_name = change.name;
                entry.source_kind = 'passive_ability';
                store.splice(at, 0, entry); at += 1;
            }
            defenses[spec.slot] = store;
        }
    }
    return { ok: true, defenses: Object.keys(defenses).length ? defenses : undefined };
}

/** derive_damage_source_kind (damage_dice_meta.php) — the source_kind stamped on a host-attributed defense entry. */
export function deriveDamageSourceKind(itemType: string): string | null {
    switch (itemType) {
        case 'weapon': case 'natural_weapon': return 'weapon';
        case 'passive_ability': return 'passive_ability';
        case 'status_effect': return 'status_effect';
        case 'attack_ability': case 'opportunity_attack_ability': case 'ability': case 'full_ability': case 'quick_ability': case 'free_ability': case 'reaction_ability': return 'ability';
        case 'spell': case 'cantrip': return 'spell';
        case 'creature_type': case 'creature_sub_type': case 'creature_sub_type_ancestral': case 'creature_sub_type_grouping': case 'creature_ancestry': case 'creature_grouping': case 'creature_lineage': return 'creature_type';
        case 'ancestry': case 'lineage': return 'ancestry';
        case 'class': return 'class';
        case 'subclass': return 'subclass';
        case 'background': return 'background';
        case 'feat': return 'class';
        case '': return null;
        default: return itemType;
    }
}

export const STATUS_DAMAGE_DEFENSE_LAYERS = ['damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability'] as const;
/** The contributor list a first-wins host entry gains when it is created AHEAD of an existing provider's entry (2026-09-28, CMP2):
 *  that provider's own normalized meta, then its all_sources in order — skipping any whose source_item_id is the host's or already
 *  listed (PHP's `$already`; ft_normalize_defense_source_meta keeps only non-empty scalar fields, as strings). */
export function foldDefenseContributors(existing: Row, hostId: string): Row[] {
    const out: Row[] = [];
    const seen = new Set<string>([upper(hostId)]);
    const push = (m: Row): void => {
        const n: Row = {};
        for (const k of ['source_item_id', 'source_name', 'source_kind', 'source_origin_item_id', 'source_origin_name']) {
            const v = m[k];
            if (v !== undefined && v !== null && v !== '' && v !== false && v !== 0 && v !== '0' && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean')) n[k] = v === true ? '1' : String(v);
        }
        const sid = upper(n.source_item_id);
        if (sid !== '' && seen.has(sid)) return;
        if (!Object.keys(n).length) return;
        if (sid !== '') seen.add(sid);
        out.push(n);
    };
    push(existing);
    for (const c of Array.isArray(existing.all_sources) ? existing.all_sources : []) if (isObj(c)) push(c as Row);
    return out;
}
export interface StatusDamageDefenseInput {
    defenses: Row | undefined;
    host: { id: string; name: string; kind: string | null };
    applied: boolean;
    /** the status definition's OWN fragments per damage-type layer (authored order) */
    fragments: Record<string, unknown[]>;
    /** damage types per layer granted by the host's own authored layers, and by every OTHER target status held after the mutation */
    hostOwn: Record<string, string[]>;
    otherHeld: Record<string, string[]>;
}
/**
 * 2026-09-24 late (C1 status lane — cheap single layers): the STATUS share of finalize_creature_arr's damage-type defense fold.
 * layer_on_status_effects' catch-all appends a held status's damage_type_* values onto the host ROOT; finalize folds that root residue
 * FIRST (before permanent statuses, feats, passives) through add_damage_type_defense_entry, attributed to the HOST itself (bare UUIDs
 * take $self_source_meta), first-wins per damage type. All root providers share the host attribution; independent contributors in
 * all_sources remain when the last root share leaves:
 *   apply  → the new host entries go FIRST, in fragment order;
 *   remove → another root share keeps the entry; otherwise a remaining contributor takes its slot.
 */
export function applyStatusDamageDefenseDelta(input: StatusDamageDefenseInput): DefenseDeltaResult {
    const defenses: Row = { ...input.defenses };
    const hostId = upper(input.host.id);
    for (const layer of STATUS_DAMAGE_DEFENSE_LAYERS) {
        const seen = new Set<string>();
        const types = (input.fragments[layer] ?? []).map(f => upper(entryUuid(f, 'damage_type'))).filter(t => t !== '' && !seen.has(t) && (seen.add(t), true));
        if (!types.length) continue;
        const slot = DEFENSE_SLOTS[layer]!.slot;
        const store = Array.isArray(defenses[slot]) ? (defenses[slot] as unknown[]).slice() : [];
        const typeOf = (e: unknown): string => isObj(e) ? upper(e.damage_type) : upper(e);
        if (input.applied) {
            if (store.some(e => !isObj(e))) return { ok: false, reason: `unported:status-defense:shape:${layer}` };
            // 2026-09-28 (CMP2, the `dup` family): a type another provider already grants — add_damage_type_defense_entry is first-wins
            // and the status's host share is folded FIRST, so the host entry takes the front and the earlier provider (then its own
            // contributors) folds into all_sources (the stored row elides an empty all_sources — measured: Resistant / Shielded / Chilled)
            let rest = store;
            const fresh = types.map(t => {
                const entry: Row = { damage_type: t };
                if (input.host.id) entry.source_item_id = input.host.id;
                if (input.host.name) entry.source_name = input.host.name;
                if (input.host.kind) entry.source_kind = input.host.kind;
                const i = rest.findIndex(e => typeOf(e) === t);
                if (i >= 0) { const folded = foldDefenseContributors(rest[i] as Row, hostId); if (folded.length) entry.all_sources = folded; rest = rest.filter((_, k) => k !== i); }
                return entry;
            });
            defenses[slot] = [...fresh, ...rest];
        } else {
            let next = store;
            const retained = new Set([...(input.hostOwn[layer] ?? []), ...(input.otherHeld[layer] ?? [])].map(upper));
            for (const t of types) {
                const i = next.findIndex(e => typeOf(e) === t);
                if (i < 0) return { ok: false, reason: `unported:status-defense:missing:${layer}` };
                const e = next[i];
                if (!isObj(e) || upper(e.source_item_id) !== hostId) return { ok: false, reason: `unported:status-defense:shared:${layer}` };
                // Another root provider folds to this same host entry, with the same attribution and contributors.
                if (retained.has(t)) continue;
                if (e.all_sources !== undefined && !Array.isArray(e.all_sources)) return { ok: false, reason: `unported:status-defense:shape:${layer}` };
                const remaining = Array.isArray(e.all_sources) ? e.all_sources : [];
                if (remaining.some(source => !isObj(source) || !source.source_item_id || upper(source.source_item_id) === hostId))
                    return { ok: false, reason: `unported:status-defense:shared:${layer}` };
                if (remaining.length) {
                    const [first, ...rest] = remaining as Row[];
                    next[i] = { damage_type: t, ...first, ...(rest.length ? { all_sources: rest } : {}) };
                } else next = next.filter((_, k) => k !== i);
            }
            if (next.length) defenses[slot] = next; else delete defenses[slot];
        }
    }
    return { ok: true, defenses: Object.keys(defenses).length ? defenses : undefined };
}
